import tinycolor from "tinycolor2";
import {
  normalizeColorValue,
  isValidCssColor,
  isValidHexColor,
  parseSpaceSeparatedHsl,
  cleanBgColorValue,
  parseColorValue,
} from "@/components/RightStyle/TailwindStylingPanel/components/input/color-picker/utils/colorFormatUtils";
import type { ColorToken, ColorGroup } from "../types";
import type { ComboBoxOptionType } from "@/components/RightStyle/TailwindStylingPanel/components/input";
import { useDesignTokensStore } from "../store/designTokensStore";
import { useDarkModeStore } from "@/store/zustand/darkModeStore";
import { resolveDesignTokenColor } from "@/tailwind/tailwindUtils";

/**
 * Helper function to create a ColorToken with proper structure
 */
export function createColorToken(
  name: string,
  colorValue: string,
  cssCustomProperties?: Record<string, string>,
  group?: string | null,
): ColorToken {
  const resolved = resolveColorValue(colorValue, cssCustomProperties);

  let tailwindClass = `${group || ""}`;
  if (name?.toLowerCase() !== "default") {
    tailwindClass += group ? `-${name.toLowerCase()}` : name.toLowerCase();
  }

  return {
    name,
    group: group || null,
    value: colorValue,
    color: resolved.hexColor,
    tailwindClass,
    hexColor: resolved.hexColor,
    hasError: resolved.hasError,
  };
}

/**
 * Resolves a color value to both Tailwind class name and hex color
 * Returns an object with the Tailwind class name, hex color, and error state
 */
export function resolveColorValue(
  colorValue: string,
  cssCustomProperties?: Record<string, string>,
): {
  hexColor: string;
  hasError: boolean;
} {
  const normalizedValue = normalizeColorValue(colorValue);

  // If it's already a valid hex color, use it as both class and hex
  if (isValidHexColor(normalizedValue.trim())) {
    return {
      hexColor: normalizedValue,
      hasError: false,
    };
  }

  // If it's a CSS variable in var(--name) format, try to convert to Tailwind class
  if (normalizedValue.includes("var(--")) {
    const varMatch = normalizedValue.match(/var\(--([^)]+)\)/);
    if (varMatch) {
      const variableName = varMatch[1];
      const fullVarName = `--${variableName}`;

      // Try to get the actual hex value from CSS custom properties
      let hexColor = normalizedValue; // fallback
      let wasResolved = false;

      if (cssCustomProperties?.[fullVarName]) {
        const cssValue = cssCustomProperties[fullVarName];

        // First try to parse space-separated HSL values
        const parsedHex = parseSpaceSeparatedHsl(cssValue);
        if (parsedHex) {
          hexColor = parsedHex;
          wasResolved = true;
        } else if (isValidCssColor(cssValue)) {
          // If it's a valid CSS color, convert to hex
          const tc = tinycolor(cssValue);
          hexColor = tc.toHexString();
          wasResolved = true;
        } else {
          // Some CSS values might be space-separated RGB values like "255 255 255"
          // Try to parse as space-separated RGB
          const parts = cssValue.trim().split(/\s+/);
          if (
            parts.length === 3 &&
            parts.every((p) => !Number.isNaN(Number(p)))
          ) {
            const rgbValue = `rgb(${parts.join(", ")})`;
            const tcRgb = tinycolor(rgbValue);
            if (tcRgb.isValid()) {
              hexColor = tcRgb.toHexString();
              wasResolved = true;
            } else {
              hexColor = cssValue;
              wasResolved = false;
            }
          } else {
            // Keep the original value if we can't convert it
            hexColor = cssValue;
            wasResolved = false;
          }
        }
      }

      // If the CSS variable couldn't be resolved or the resolved value is not a valid color, it's an error
      const hasError =
        !wasResolved || (wasResolved && !tinycolor(hexColor).isValid());

      // Debug logging for CSS variables that couldn't be resolved
      if (hasError && cssCustomProperties?.[fullVarName]) {
        console.warn(
          `Failed to parse CSS variable ${fullVarName}:`,
          cssCustomProperties[fullVarName],
          "-> hexColor:",
          hexColor,
          "wasResolved:",
          wasResolved,
        );
      }

      return {
        hexColor,
        hasError,
      };
    }
  }

  // If it's still a CSS variable that couldn't be parsed, mark as error
  if (normalizedValue.includes("var(--")) {
    return {
      hexColor: normalizedValue,
      hasError: true,
    };
  }

  // For other color formats (rgb, hsl, etc.), try to validate with tinycolor
  const tc = tinycolor(normalizedValue);
  if (tc.isValid()) {
    const hexColor = tc.toHexString();
    return {
      hexColor: hexColor,
      hasError: false,
    };
  }

  // If we can't resolve it to a valid color, mark as error
  return {
    hexColor: normalizedValue,
    hasError: true,
  };
}

/**
 * Parse colors from backend API response
 * Backend sends nested object structure (e.g., { primary: { DEFAULT: "value", foreground: "value" } })
 * or flat structure for ungrouped colors (e.g., { border: "value" })
 * Handles both light and dark colors separately
 */
export function parseObjectColorsToGroups(
  lightColors: Record<string, string | Record<string, string>>,
  darkColors?: Record<string, string | Record<string, string>>,
  lightCssCustomProperties?: Record<string, string>,
  darkCssCustomProperties?: Record<string, string>,
): {
  groups: ColorGroup[];
  ungroupedColors: ColorToken[];
  darkGroups: ColorGroup[];
  darkUngroupedColors: ColorToken[];
} {
  const groupMap = new Map<string, ColorToken[]>();
  const ungroupedColors: ColorToken[] = [];
  const darkGroupMap = new Map<string, ColorToken[]>();
  const darkUngroupedColors: ColorToken[] = [];

  // Helper function to process colors from nested or flat structure
  const processColors = (
    colors: Record<string, string | Record<string, string>>,
    targetGroupMap: Map<string, ColorToken[]>,
    targetUngroupedColors: ColorToken[],
    cssProps?: Record<string, string>,
  ) => {
    for (const [colorKey, colorValue] of Object.entries(colors)) {
      if (typeof colorValue === "string") {
        // Flat structure - ungrouped color
        const resolved = resolveColorValue(colorValue, cssProps);
        const colorToken: ColorToken = {
          name: colorKey,
          group: null, // Ungrouped colors have no group
          value: colorValue, // Keep original value
          color: resolved.hexColor, // Backward compatibility
          tailwindClass: colorKey, // For ungrouped colors, use the colorKey as tailwindClass
          hexColor: resolved.hexColor,
          hasError: resolved.hasError,
        };

        targetUngroupedColors.push(colorToken);
      } else if (typeof colorValue === "object" && colorValue !== null) {
        // Nested structure - grouped colors
        const groupName = colorKey;

        if (!targetGroupMap.has(groupName)) {
          targetGroupMap.set(groupName, []);
        }

        const groupColors = targetGroupMap.get(groupName);
        if (!groupColors) continue;

        for (const [shadeName, shadeValue] of Object.entries(colorValue)) {
          if (typeof shadeValue === "string") {
            const resolved = resolveColorValue(shadeValue, cssProps);

            let tailwindClass = `${groupName}`;
            if (shadeName?.toLowerCase() !== "default") {
              tailwindClass += `-${shadeName.toLowerCase()}`;
            }

            const colorToken: ColorToken = {
              // Preserve explicit default shade as "default" instead of renaming to the group name
              name: shadeName.toLowerCase(),
              group: groupName, // Grouped colors belong to their group
              value: shadeValue, // Keep original value
              color: resolved.hexColor, // Backward compatibility
              tailwindClass, // Use the computed tailwindClass instead of resolved one
              hexColor: resolved.hexColor,
              hasError: resolved.hasError,
            };
            groupColors.push(colorToken);
          }
        }
      }
    }
  };

  // Process light colors with light CSS properties
  if (lightColors) {
    processColors(
      lightColors,
      groupMap,
      ungroupedColors,
      lightCssCustomProperties,
    );
  }

  // Process dark colors with dark CSS properties
  if (darkColors && Object.keys(darkColors).length > 0) {
    // When we have explicit dark colors, we need to:
    // 1. First create virtual dark tokens ONLY for light tokens that don't have explicit dark versions
    // 2. Then add the explicit dark tokens

    // Helper to check if a token exists in darkColors (handles both flat and nested)
    const hasExplicitDarkToken = (key: string, shade?: string) => {
      if (shade) {
        // Grouped token
        return (
          darkColors[key] &&
          typeof darkColors[key] === "object" &&
          darkColors[key][shade]
        );
      }
      // Ungrouped token
      return darkColors[key] !== undefined;
    };

    // Step 1: Process light tokens with dark CSS properties, but only for tokens without explicit dark versions
    if (
      darkCssCustomProperties &&
      Object.keys(darkCssCustomProperties).length > 0
    ) {
      // Filter light colors to only include those without explicit dark versions
      const lightColorsWithoutDarkVersions: Record<
        string,
        string | Record<string, string>
      > = {};

      for (const [key, value] of Object.entries(lightColors)) {
        if (typeof value === "string") {
          // Ungrouped token
          if (!hasExplicitDarkToken(key)) {
            lightColorsWithoutDarkVersions[key] = value;
          }
        } else if (typeof value === "object" && value !== null) {
          // Grouped token
          const filteredGroup: Record<string, string> = {};
          for (const [shade, shadeValue] of Object.entries(value)) {
            if (
              typeof shadeValue === "string" &&
              !hasExplicitDarkToken(key, shade)
            ) {
              filteredGroup[shade] = shadeValue;
            }
          }
          if (Object.keys(filteredGroup).length > 0) {
            lightColorsWithoutDarkVersions[key] = filteredGroup;
          }
        }
      }

      processColors(
        lightColorsWithoutDarkVersions,
        darkGroupMap,
        darkUngroupedColors,
        darkCssCustomProperties,
      );
    } else {
      // If no dark CSS properties, copy light tokens as fallback (only those without explicit dark versions)
      for (const [groupName, colors] of groupMap.entries()) {
        const filteredColors = colors.filter(
          (color) => !hasExplicitDarkToken(groupName, color.name),
        );
        if (filteredColors.length > 0) {
          darkGroupMap.set(groupName, filteredColors);
        }
      }

      const filteredUngroupedColors = ungroupedColors.filter(
        (color) => !hasExplicitDarkToken(color.name),
      );
      darkUngroupedColors.push(...filteredUngroupedColors);
    }

    // Step 2: Now add explicit dark tokens from the API
    processColors(
      darkColors,
      darkGroupMap,
      darkUngroupedColors,
      darkCssCustomProperties,
    );
  } else if (
    darkCssCustomProperties &&
    Object.keys(darkCssCustomProperties).length > 0
  ) {
    // If no explicit dark colors but we have dark CSS properties,
    // create "virtual" dark colors using the same structure as light colors
    // but resolved with dark CSS properties
    processColors(
      lightColors,
      darkGroupMap,
      darkUngroupedColors,
      darkCssCustomProperties,
    );
  }

  // Convert group maps to ColorGroup arrays with consistent sorting
  const groups: ColorGroup[] = Array.from(groupMap.entries())
    .sort(([a], [b]) => a.localeCompare(b)) // Sort groups alphabetically
    .map(([groupName, colors]) => ({
      name: groupName,
      isExpanded: true, // Default to expanded
      colors: colors.sort((a, b) => a.name.localeCompare(b.name)), // Sort colors within group
    }));

  const darkGroups: ColorGroup[] = Array.from(darkGroupMap.entries())
    .sort(([a], [b]) => a.localeCompare(b)) // Sort groups alphabetically
    .map(([groupName, colors]) => ({
      name: groupName,
      isExpanded: true, // Default to expanded
      colors: colors.sort((a, b) => a.name.localeCompare(b.name)), // Sort colors within group
    }));

  // Sort ungrouped colors alphabetically
  ungroupedColors.sort((a, b) => a.name.localeCompare(b.name));
  darkUngroupedColors.sort((a, b) => a.name.localeCompare(b.name));

  return { groups, ungroupedColors, darkGroups, darkUngroupedColors };
}

/**
 * Creates merged color groups that combine light and dark tokens into a single unified view
 * This allows displaying all tokens regardless of current mode, with proper fallbacks
 */
export function createMergedGroups(
  lightGroups: ColorGroup[],
  lightColors: ColorToken[],
  darkGroups: ColorGroup[],
  darkColors: ColorToken[],
  isViewDarkMode: boolean,
): { groups: ColorGroup[]; colors: ColorToken[] } {
  const mergedGroupsMap = new Map<string, ColorGroup>();
  const mergedColorsMap = new Map<string, ColorToken>();

  // Helper to add token to merged collection, preferring current mode but falling back
  const addTokenWithFallback = (
    lightToken: ColorToken | undefined,
    darkToken: ColorToken | undefined,
    groupName?: string,
  ) => {
    const preferredToken = isViewDarkMode ? darkToken : lightToken;
    const fallbackToken = isViewDarkMode ? lightToken : darkToken;
    const tokenToUse = preferredToken || fallbackToken;

    if (!tokenToUse) return;

    // Only show "?" fallback indicator when:
    // - We're in light mode (not dark mode) AND there's a dark token but no light token
    // This means we're viewing a dark-only token in light mode
    const isFallback = !isViewDarkMode && !lightToken && !!darkToken;

    const tokenWithFallbackFlag = {
      ...tokenToUse,
      isFallback,
    };

    if (groupName) {
      // Add to group
      if (!mergedGroupsMap.has(groupName)) {
        mergedGroupsMap.set(groupName, {
          name: groupName,
          colors: [],
          isExpanded: true, // Default to expanded for new groups
        });
      }
      const group = mergedGroupsMap.get(groupName);
      if (!group) {
        return;
      }

      // Check if this token already exists in the group
      const existingIndex = group.colors.findIndex(
        (c) => c.name === tokenWithFallbackFlag.name,
      );
      if (existingIndex >= 0) {
        // Replace with preferred token
        group.colors[existingIndex] = tokenWithFallbackFlag;
      } else {
        // Add new token in sorted position
        const insertIndex = group.colors.findIndex(
          (c) => c.name.localeCompare(tokenWithFallbackFlag.name) > 0,
        );
        if (insertIndex === -1) {
          group.colors.push(tokenWithFallbackFlag);
        } else {
          group.colors.splice(insertIndex, 0, tokenWithFallbackFlag);
        }
      }
    } else {
      // Add to ungrouped colors
      mergedColorsMap.set(tokenWithFallbackFlag.name, tokenWithFallbackFlag);
    }
  };

  // Process light tokens
  for (const group of lightGroups) {
    for (const color of group.colors) {
      const darkGroup = darkGroups.find((g) => g.name === group.name);
      const darkColor = darkGroup?.colors.find((c) => c.name === color.name);
      addTokenWithFallback(color, darkColor, group.name);
    }
  }

  // Process dark tokens that might not have light counterparts
  for (const group of darkGroups) {
    for (const color of group.colors) {
      const lightGroup = lightGroups.find((g) => g.name === group.name);
      const lightColor = lightGroup?.colors.find((c) => c.name === color.name);
      // Only add if we haven't already processed this token
      if (!lightColor) {
        addTokenWithFallback(undefined, color, group.name);
      }
    }
  }

  // Process ungrouped colors
  for (const color of lightColors) {
    const darkColor = darkColors.find((c) => c.name === color.name);
    addTokenWithFallback(color, darkColor);
  }

  // Process dark ungrouped colors that might not have light counterparts
  for (const color of darkColors) {
    const lightColor = lightColors.find((c) => c.name === color.name);
    if (!lightColor) {
      addTokenWithFallback(undefined, color);
    }
  }

  // Convert maps to arrays and sort
  const groups = Array.from(mergedGroupsMap.values()).sort((a, b) =>
    a.name.localeCompare(b.name),
  );
  const colors = Array.from(mergedColorsMap.values()).sort((a, b) =>
    a.name.localeCompare(b.name),
  );

  return { groups, colors };
}

/**
 * Extract color value from a ComboBox option
 */
export function extractColorValueFromOption(
  option: ComboBoxOptionType,
  backgroundColorProperties: ComboBoxOptionType[],
): string {
  let colorValue = "";

  if (option.css) {
    colorValue = cleanBgColorValue(option.css);
  } else if (option.tailwind) {
    const parsedColor = parseColorValue(
      option.tailwind,
      backgroundColorProperties,
    );
    colorValue = parsedColor.isValid ? parsedColor.hex : "";
  }

  return colorValue;
}

/**
 * Build arbitrary Tailwind class for color picker
 */
export function buildArbitraryClass(input: string): ComboBoxOptionType {
  return {
    tailwind: `bg-[${input}]`,
    css: `background-color: ${input}`,
  };
}

export const resolveColor = (
  canvasId: string,
  classes: string[],
): string | null => {
  const { lightColorGroups, lightColors, darkColorGroups, darkColors } =
    useDesignTokensStore.getState();

  const canvasDarkMode =
    useDarkModeStore.getState().canvasDarkModeStates[canvasId] || false;

  return resolveDesignTokenColor(
    classes,
    lightColorGroups,
    lightColors,
    darkColorGroups,
    darkColors,
    canvasDarkMode,
  );
};
